import { gql, TypedDocumentNode } from "@apollo/client";
import { PHONE_NUMBER_FRAGMENT, ADDRESS_FRAGMENT } from "@/fragments";
import {
  Address,
  FacilityCourseType,
  FacilityOperatingStatus,
  FacilityOperationType,
  FacilityType,
  PhoneNumber,
  EnumOptions
} from "@/types";

export type FacilityGeneralManager =
  | {
      __typename: "BackofficePerson";
      id: string;
      firstName: string;
      lastName: string;
      informalName: string;
      preferredLastName: string;
      suffixName: string;
      displayName: string;
    }
  | {
      __typename: "BackofficeLegacyFacilityGeneralManager";
      id: string;
      name: string;
    };

export type FacilityDetailsContactQueryResponse = {
  facility: {
    id: string;
    name: string;
    legacyId: string | null;
    section: { id: string; name: string } | null;
    chapter: { id: string; name: string } | null;
    operatingStatus: FacilityOperatingStatus | null;
    facilityType: FacilityType;
    courseType: FacilityCourseType | null;
    operationType: FacilityOperationType | null;
    phone: PhoneNumber | null;
    fax: PhoneNumber | null;
    golfShopPhone: PhoneNumber | null;
    mainAddress: Address | null;
    mailingAddress: Address | null;
    website: string | null;
    highestRankedEmployment: {
      id: string;
      title: string;
      person: {
        pgaId: string;
        id: string;
        displayName: string;
        informalName?: string;
      };
    } | null;
    parentCompany: { id: string; name: string } | null;
    generalManager: FacilityGeneralManager | null;
    managementGroup: string | null;
    lengthOfSeasonInMonths: number | null;
    holesInRegulation: number | null;
    practiceRangeAvailable: boolean | null;
    simulatorAvailable: boolean | null;
    golfCartBrand: string | null;
    membershipTotal: number | null;
    membershipMen: number | null;
    membershipWomen: number | null;
    membershipJunior: number | null;
    membershipSenior: number | null;
  };
  operatingStatusOptions: EnumOptions<FacilityOperatingStatus>;
  facilityTypeOptions: EnumOptions<FacilityType>;
  courseTypeOptions: EnumOptions<FacilityCourseType>;
  operationTypeOptions: EnumOptions<FacilityOperationType>;
};

export type FacilityAffiliationChangeImpactResponse = {
  facilityAffiliationChangeImpact: {
    currentChapter: { id: string; name: string } | null;
    proposedChapter: { id: string; name: string } | null;
    sectionChange: boolean;
    chapterChange: boolean;
    affectedOnSitePeopleCount: number;
    excludedRemotePeopleCount: number;
    activeMembersRequiringBandReviewCount: number;
    chapterOnlyUpdatesCount: number;
    requiresManualReview: boolean;
    noteRequired: boolean;
    people: {
      person: {
        id: string;
        pgaId: string | null;
        displayName: string;
      };
      requiresBandReview: boolean;
      reason: string;
    }[];
  };
};

export type FacilityAffiliationChangeImpactVariables = {
  facilityId: string;
  sectionId?: string | null;
  chapterId?: string | null;
  peopleLimit?: number;
};

export type PersonPrimaryEmploymentResponse = {
  person: {
    id: string;
    employments: { id: string; isPrimary: boolean }[];
  } | null;
};

export type PersonPrimaryEmploymentVariables = {
  pgaId: string;
};

export const PERSON_PRIMARY_EMPLOYMENT_QUERY: TypedDocumentNode<
  PersonPrimaryEmploymentResponse,
  PersonPrimaryEmploymentVariables
> = gql`
  query PersonPrimaryEmployment($pgaId: ID!) {
    person(pgaId: $pgaId) {
      id
      employments {
        id
        isPrimary
      }
    }
  }
`;

const FACILITY_DETAILS_CONTACT_FRAGMENT = gql`
  fragment FacilityDetailsContactFragment on BackofficeFacility {
    id
    name
    legacyId
    section {
      id
      name
    }
    chapter {
      id
      name
    }
    operatingStatus
    facilityType
    courseType
    operationType
    phone {
      ...PhoneNumberFragment
    }
    fax {
      ...PhoneNumberFragment
    }
    golfShopPhone {
      ...PhoneNumberFragment
    }
    mainAddress {
      ...AddressFragment
    }
    mailingAddress {
      ...AddressFragment
    }
    website
    highestRankedEmployment {
      id
      title
      person {
        id
        pgaId
        displayName
      }
    }
    parentCompany {
      id
      name
    }
    generalManager {
      __typename
      ... on BackofficePerson {
        id
        firstName
        lastName
        informalName
        preferredLastName
        suffixName
        displayName
      }
      ... on BackofficeLegacyFacilityGeneralManager {
        name
      }
    }
    managementGroup
    lengthOfSeasonInMonths
    holesInRegulation
    practiceRangeAvailable
    simulatorAvailable
    golfCartBrand
    membershipTotal
    membershipMen
    membershipWomen
    membershipJunior
    membershipSenior
  }
  ${PHONE_NUMBER_FRAGMENT}
  ${ADDRESS_FRAGMENT}
`;

export const FACILITY_DETAILS_CONTACT_QUERY = gql`
  query GetFacilityContactInformation($facilityId: ID!) {
    facility(id: $facilityId) {
      ...FacilityDetailsContactFragment
    }
    operatingStatusOptions: __type(name: "FacilityOperatingStatus") {
      enumValues {
        name
        description
      }
    }
    facilityTypeOptions: __type(name: "FacilityType") {
      enumValues {
        name
        description
      }
    }
    courseTypeOptions: __type(name: "FacilityCourseType") {
      enumValues {
        name
        description
      }
    }
    operationTypeOptions: __type(name: "FacilityOperationType") {
      enumValues {
        name
        description
      }
    }
  }
  ${FACILITY_DETAILS_CONTACT_FRAGMENT}
`;

export const PATCH_FACILITY_MUTATION = gql`
  mutation PatchFacility($input: BackofficePatchFacilityInput!) {
    patchFacility(input: $input) {
      success
      message
      facility {
        ...FacilityDetailsContactFragment
      }
    }
  }
  ${FACILITY_DETAILS_CONTACT_FRAGMENT}
`;

export const FACILITY_AFFILIATION_CHANGE_IMPACT_QUERY: TypedDocumentNode<
  FacilityAffiliationChangeImpactResponse,
  FacilityAffiliationChangeImpactVariables
> = gql`
  query FacilityAffiliationChangeImpact($facilityId: ID!, $sectionId: ID, $chapterId: ID, $peopleLimit: Int = 5) {
    facilityAffiliationChangeImpact(
      facilityId: $facilityId
      sectionId: $sectionId
      chapterId: $chapterId
      peopleLimit: $peopleLimit
    ) {
      sectionChange
      chapterChange
      affectedOnSitePeopleCount
      excludedRemotePeopleCount
      activeMembersRequiringBandReviewCount
      chapterOnlyUpdatesCount
      requiresManualReview
      noteRequired
      people {
        person {
          id
          pgaId
          displayName
        }
        requiresBandReview
        reason
      }
      currentChapter {
        id
        name
      }
      proposedChapter {
        id
        name
      }
    }
  }
`;
